import React from "react";
import { timeOnly } from "@/lib/dates";
import { PhoneIncoming, PhoneMissed, PhoneOff, Phone, X, Bell } from "lucide-react";
import Avatar from "@/components/Avatar";

// The callee's side of a call. A fixed card, not a modal: it stays until
// answered or declined, and it does not stop you working meanwhile.
//
// There is deliberately no "ringing…" or "answered" on the caller's side.
// kMeet is external, so the server never learns who entered the room, and a
// state it cannot observe would be fiction. "Missed" is local to this end:
// nobody answered here within the ring window. The card in the conversation
// stays as the trace; this is the mechanism.
export default function IncomingCall({ call, onAccept, onDecline, onCallBack, onDismiss,
                                      onEnableNotifications, onDeclineNotifications }) {
  const missed = call.status === "missed";
  const name = call.from?.name || "Un coleg";
  const when = timeOnly(call.at);
  const line = missed
    ? `Apel pierdut · ${when}`
    : call.kind === "invite" ? "te invită într-un apel" : "te sună";
  // The browser keeps audio suspended until the page has been clicked once. The
  // card is visible either way; without this line the silence looks like a bug
  // in the app rather than a permission the page has not been given yet.
  const muted = !missed && call.muted;

  return (
    <div
      role="alertdialog" aria-live="assertive" aria-label={missed ? "Apel pierdut" : "Apel primit"}
      data-testid="incoming-call" data-status={call.status}
      className="fixed bottom-4 right-4 z-[60] w-80 bg-surface border border-line/10 shadow-2xl fade-in"
    >
      <div className="flex items-center gap-3 px-4 py-3 border-b border-line/10">
        <span className={missed ? "text-fg/50" : "text-brand animate-pulse"}>
          {missed ? <PhoneMissed className="w-4 h-4" /> : <PhoneIncoming className="w-4 h-4" />}
        </span>
        <Avatar user={{ id: call.from?.id, name }} size={28} />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold truncate">{name}</p>
          <p className="text-xs text-fg/60">{line}</p>
        </div>
        {missed && (
          <button type="button" onClick={onDismiss} className="p-1 text-fg/40 hover:text-fg" aria-label="Închide" data-testid="incoming-dismiss">
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      {muted && (
        <p className="px-4 pt-2 text-[11px] text-fg/50" data-testid="incoming-muted">
          Fără sunet — browserul pornește audio abia după un click în pagină.
        </p>
      )}
      {missed && call.offerNotifications && (
        // Asked here and nowhere else. The evidence is on screen: a call was
        // just lost. A prompt at login has no evidence and gets refused.
        <div className="px-4 pt-3 pb-1 border-t border-line/10" data-testid="incoming-notify-offer">
          <p className="text-xs text-fg/70 leading-relaxed">
            Ai pierdut un apel de la {name}. Vrei să fii anunțat data viitoare,
            chiar dacă Qedara Chat nu e deschis?
          </p>
          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onDeclineNotifications} data-testid="incoming-notify-no"
              className="btn-ghost text-xs px-3 py-1.5 flex-1">Nu, mulțumesc</button>
            <button type="button" onClick={onEnableNotifications} data-testid="incoming-notify-yes"
              className="btn-primary text-xs px-3 py-1.5 flex-1 flex items-center justify-center gap-1.5">
              <Bell className="w-3.5 h-3.5" /> Anunță-mă
            </button>
          </div>
        </div>
      )}
      <div className="flex gap-2 p-3">
        {missed ? (
          <>
            <button type="button" onClick={onDismiss} className="btn-ghost text-sm px-3 py-1.5 flex-1" data-testid="incoming-close">Închide</button>
            {onCallBack && (
              <button type="button" onClick={onCallBack} className="btn-primary text-sm px-3 py-1.5 flex-1 flex items-center justify-center gap-1.5" data-testid="incoming-callback">
                <Phone className="w-3.5 h-3.5" /> Sună înapoi
              </button>
            )}
          </>
        ) : (
          <>
            <button type="button" onClick={onDecline} className="text-sm px-3 py-1.5 flex-1 flex items-center justify-center gap-1.5 bg-red-600 text-white hover:bg-red-500" data-testid="incoming-decline">
              <PhoneOff className="w-3.5 h-3.5" /> Refuză
            </button>
            <button type="button" onClick={onAccept} className="btn-primary text-sm px-3 py-1.5 flex-1 flex items-center justify-center gap-1.5" data-testid="incoming-accept">
              <Phone className="w-3.5 h-3.5" /> Răspunde
            </button>
          </>
        )}
      </div>
    </div>
  );
}
